import React, { useCallback, useMemo, useState } from 'react';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import AddIcon from '@mui/icons-material/Add';
import ClearIcon from '@mui/icons-material/Clear';
import DeleteIcon from '@mui/icons-material/Delete';
import RestoreIcon from '@mui/icons-material/Restore';
import SaveIcon from '@mui/icons-material/Save';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  IconButton,
  MenuItem,
  Link as MuiLink,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { blue, red } from '@mui/material/colors';
import { GridColDef, GridRenderCellParams, useGridApiRef } from '@mui/x-data-grid';

import {
  InlineCheckboxEditCell,
  InlineNumberEditCell,
  InlineTextFieldEditCell,
} from '~/components/Accommodation/utils/datagrid';
import { useConfirmDialog } from '~/components/Common/Providers/ConfirmDialogProvider';
import Table from '~/components/v2/common/Table/Table';

import * as AccommodationService from '~/services/AccommodationService';

import { BaseCatalogItem, CatalogItemAssociationConfig, CatalogItemStatus } from '../../../types/catalogItem';

const NEW_ROW_CODE_PREFIX = 'new-';

interface CatalogGroupOption {
  id: string;
  name: string;
  code: string;
}

interface CatalogItemOption {
  code: string;
  name: string;
}

type AssociatedItemRowData<TAssociatedItem extends BaseCatalogItem> = TAssociatedItem & {
  isNew?: boolean;
};

interface PendingChanges {
  facilityCode?: string;
  amenityCode?: string;
  displayOrder?: number | null;
  featured?: boolean | null;
  value?: string | null;
}

interface CatalogItemAssociationGridProps<
  TItem extends BaseCatalogItem,
  TAssociatedItem extends BaseCatalogItem,
  TAddPayload,
  TUpdatePayload,
> {
  config: CatalogItemAssociationConfig<TItem, TAssociatedItem, TAddPayload, TUpdatePayload>;
  pathParams: Record<string, string>;
  pageSize?: number;
}

export default function CatalogItemAssociationGrid<
  TItem extends BaseCatalogItem,
  TAssociatedItem extends BaseCatalogItem,
  TAddPayload,
  TUpdatePayload,
>({
  config,
  pathParams,
  pageSize = 50,
}: CatalogItemAssociationGridProps<TItem, TAssociatedItem, TAddPayload, TUpdatePayload>) {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const apiRef = useGridApiRef();

  const [modifiedRows, setModifiedRows] = useState<Record<string, PendingChanges>>({});
  const [localRows, setLocalRows] = useState<Array<AssociatedItemRowData<TAssociatedItem>>>([]);
  const [deletingCodes, setDeletingCodes] = useState<Set<string>>(new Set());

  const showConfirmDialog = useConfirmDialog();

  // Determine the item code key based on item type
  const itemCodeKey = config.itemName.toLowerCase() === 'facility' ? 'facilityCode' : 'amenityCode';
  const catalogGroupType = config.itemName.toLowerCase() === 'facility' ? 'facility' : 'amenity';

  // Filter and pagination state
  const [page, setPage] = useState(0);
  const [searchName, setSearchName] = useState('');
  const [filterCatalogGroupId, setFilterCatalogGroupId] = useState<string | undefined>(undefined);
  const [filterStatus, setFilterStatus] = useState<CatalogItemStatus>('approved');

  // Fetch catalog groups for filter dropdown
  const { data: catalogGroupsData } = useQuery({
    queryKey: ['accommodation', 'catalog-groups', catalogGroupType],
    queryFn: async () => {
      const response = await AccommodationService.listCatalogGroups({
        type: catalogGroupType,
        limit: '1000',
        page: '1',
      });
      return response.result;
    },
  });

  const catalogGroups = catalogGroupsData?.groups ?? [];

  // Fetch all items for the dropdown
  const { data: allItemsData } = useQuery({
    queryKey: config.allItemsQueryKey,
    queryFn: async () => {
      const response = await config.listAllItems({
        status: 'approved' as const,
        limit: '1000',
        page: '1',
      });
      return response.result;
    },
  });

  const allItems = allItemsData?.items ?? [];

  // Fetch ALL associated item codes (for exclusion in dropdown)
  const { data: allAssociatedItemCodes = [] } = useQuery({
    queryKey: config.allAssociatedCodesQueryKey(pathParams[Object.keys(pathParams)[0]]),
    queryFn: async () => {
      const response = await config.getAllAssociatedItemCodes(pathParams, {
        status: 'approved' as const,
        limit: '10000',
        page: '1',
      });
      return response.result?.items?.map((item) => item.code) ?? [];
    },
    enabled: !!pathParams[Object.keys(pathParams)[0]],
  });

  // Fetch associated items (paginated for display)
  const {
    data: associatedItemsData,
    isLoading,
    error,
  } = useQuery({
    queryKey: [
      ...config.associatedItemsQueryKey(pathParams[Object.keys(pathParams)[0]]),
      { page: page + 1, searchName, filterCatalogGroupId, filterStatus },
    ],
    queryFn: async () => {
      const response = await config.listAssociatedItems(pathParams, {
        name: searchName || undefined,
        catalogGroupId: filterCatalogGroupId,
        status: filterStatus,
        limit: String(pageSize),
        page: String(page + 1),
      });
      return response.result;
    },
    enabled: !!pathParams[Object.keys(pathParams)[0]],
  });

  const associatedItems = associatedItemsData?.items ?? [];
  const totalCount = associatedItemsData?.total ?? 0;

  // Get item options that are not already added
  const availableItemOptions = useMemo(() => {
    const existingCodes = new Set(allAssociatedItemCodes);
    const pendingNewCodes = new Set(
      localRows.map((r) => (modifiedRows[r.code] as any)?.[itemCodeKey] as string).filter(Boolean),
    );
    return allItems
      .filter((item) => !existingCodes.has(item.code) && !pendingNewCodes.has(item.code))
      .map((item) => ({ code: item.code, name: item.name }));
  }, [allItems, allAssociatedItemCodes, localRows, modifiedRows, itemCodeKey]);

  // Add item association mutation
  const addMutation = useMutation({
    mutationFn: async (data: TAddPayload & { tempCode: string }) => {
      const { tempCode, ...payload } = data;
      return await config.addItem(pathParams, payload as unknown as TAddPayload);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: config.associatedItemsQueryKey(pathParams[Object.keys(pathParams)[0]]),
      });
      setLocalRows((prev) => prev.filter((r) => r.code !== variables.tempCode));
      setModifiedRows((prev) => {
        const updated = { ...prev };
        delete updated[variables.tempCode];
        return updated;
      });
      enqueueSnackbar(`${config.itemName} added to ${config.associationContext} successfully`, { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(`Error adding ${config.itemName.toLowerCase()}: ${error.message}`, { variant: 'error' });
    },
  });

  // Update item association mutation
  const updateMutation = useMutation({
    mutationFn: async ({ itemCode, data }: { itemCode: string; data: TUpdatePayload }) => {
      return await config.updateItem({ ...pathParams, [itemCodeKey]: itemCode }, data);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: config.associatedItemsQueryKey(pathParams[Object.keys(pathParams)[0]]),
      });
      setModifiedRows((prev) => {
        const updated = { ...prev };
        delete updated[variables.itemCode];
        return updated;
      });
      enqueueSnackbar(`${config.itemName} updated successfully`, { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(`Error updating ${config.itemName.toLowerCase()}: ${error.message}`, { variant: 'error' });
    },
  });

  // Remove item association mutation
  const removeMutation = useMutation({
    mutationFn: async (itemCode: string) => {
      return await config.removeItem({ ...pathParams, [itemCodeKey]: itemCode });
    },
    onSuccess: (_data, itemCode) => {
      queryClient.invalidateQueries({
        queryKey: config.associatedItemsQueryKey(pathParams[Object.keys(pathParams)[0]]),
      });
      setDeletingCodes((prev) => {
        const updated = new Set(prev);
        updated.delete(itemCode);
        return updated;
      });
      enqueueSnackbar(`${config.itemName} removed from ${config.associationContext} successfully`, {
        variant: 'success',
      });
    },
    onError: (error: Error, itemCode) => {
      setDeletingCodes((prev) => {
        const updated = new Set(prev);
        updated.delete(itemCode);
        return updated;
      });
      enqueueSnackbar(`Error removing ${config.itemName.toLowerCase()}: ${error.message}`, { variant: 'error' });
    },
  });

  const handleAddNew = useCallback(() => {
    const tempCode = `${NEW_ROW_CODE_PREFIX}${Date.now()}`;
    const newRow: AssociatedItemRowData<TAssociatedItem> = {
      code: tempCode,
      name: '',
      description: null,
      catalogGroup: undefined,
      icon: null,
      featured: null,
      displayOrder: undefined,
      isNew: true,
      status: 'approved',
    } as AssociatedItemRowData<TAssociatedItem>;
    setLocalRows((prev) => [newRow, ...prev]);
    setModifiedRows((prev) => ({
      ...prev,
      [tempCode]: { [itemCodeKey]: '' } as PendingChanges,
    }));
  }, [config]);

  const handleSave = useCallback(
    (row: AssociatedItemRowData<TAssociatedItem>) => {
      const changes = modifiedRows[row.code];
      if (!changes) return;

      if (row.isNew || row.code.startsWith(NEW_ROW_CODE_PREFIX)) {
        // Add new item association
        if (!changes[itemCodeKey as keyof PendingChanges]) {
          enqueueSnackbar(`Please select a ${config.itemName.toLowerCase()} to add`, { variant: 'error' });
          return;
        }
        addMutation.mutate({
          tempCode: row.code,
          [itemCodeKey]: changes[itemCodeKey as keyof PendingChanges],
          displayOrder: changes.displayOrder ?? undefined,
          featured: changes.featured ?? undefined,
        } as unknown as TAddPayload & { tempCode: string });
      } else {
        const valueChange =
          config.hasAssociationValue && 'value' in changes
            ? {
                value: changes.value,
              }
            : {};

        // Update existing item association
        updateMutation.mutate({
          itemCode: row.code,
          data: {
            displayOrder: changes.displayOrder,
            featured: changes.featured,
            ...valueChange,
          } as TUpdatePayload,
        });
      }
    },
    [modifiedRows, addMutation, updateMutation, enqueueSnackbar, config],
  );

  const handleClearChanges = useCallback((code: string) => {
    const isNewRow = code.startsWith(NEW_ROW_CODE_PREFIX);

    if (isNewRow) {
      setLocalRows((prev) => prev.filter((r) => r.code !== code));
    }

    setModifiedRows((prev) => {
      const updated = { ...prev };
      delete updated[code];
      return updated;
    });
  }, []);

  const handleClearOverrides = useCallback(
    (row: AssociatedItemRowData<TAssociatedItem>) => {
      updateMutation.mutate({
        itemCode: row.code,
        data: {
          displayOrder: null,
          featured: null,
          ...(config.hasAssociationValue ? { value: null } : {}),
        } as TUpdatePayload,
      });
    },
    [updateMutation, config.hasAssociationValue],
  );

  const handleDeleteClick = useCallback(
    async (item: AssociatedItemRowData<TAssociatedItem>) => {
      const isNewRow = item.code.startsWith(NEW_ROW_CODE_PREFIX);

      if (isNewRow) {
        setLocalRows((prev) => prev.filter((r) => r.code !== item.code));
        setModifiedRows((prev) => {
          const updated = { ...prev };
          delete updated[item.code];
          return updated;
        });
      } else {
        const confirmed = await showConfirmDialog({
          title: `Remove ${config.itemName}`,
          description: `Are you sure you want to remove the ${config.itemName.toLowerCase()} "${item.name}" (${
            item.code
          }) from this ${config.associationContext}?`,
        });
        if (confirmed) {
          setDeletingCodes((prev) => new Set(prev).add(item.code));
          removeMutation.mutate(item.code);
        }
      }
    },
    [showConfirmDialog, removeMutation, config],
  );

  const processRowUpdate = useCallback(
    (newRow: AssociatedItemRowData<TAssociatedItem>, _oldRow: AssociatedItemRowData<TAssociatedItem>) => {
      return newRow;
    },
    [],
  );

  const columns: Array<GridColDef<AssociatedItemRowData<TAssociatedItem>>> = useMemo(() => {
    const baseColumns: Array<GridColDef<AssociatedItemRowData<TAssociatedItem>>> = [
      {
        field: 'code',
        headerName: 'Display Name (Code)',
        flex: 0,
        width: 450,
        renderCell: (params: GridRenderCellParams<AssociatedItemRowData<TAssociatedItem>>) => {
          const isNewRow = params.row.code.startsWith(NEW_ROW_CODE_PREFIX);
          if (isNewRow) {
            const selectedCode = (modifiedRows[params.row.code] as any)?.[itemCodeKey] as string | undefined;
            const selectedItem = allItems.find((item) => item.code === selectedCode);

            return (
              <Autocomplete
                size="small"
                options={availableItemOptions}
                value={selectedItem ? { code: selectedItem.code, name: selectedItem.name } : null}
                onChange={(_event, newValue: CatalogItemOption | null) => {
                  setModifiedRows((prev) => ({
                    ...prev,
                    [params.row.code]: { ...prev[params.row.code], [itemCodeKey]: newValue?.code } as any,
                  }));
                }}
                getOptionLabel={(option) => `${option.name} (${option.code})`}
                isOptionEqualToValue={(option, value) => option.code === value.code}
                renderInput={(inputParams) => (
                  <TextField
                    {...inputParams}
                    variant="outlined"
                    placeholder={`Select ${config.itemName.toLowerCase()}`}
                  />
                )}
                fullWidth
                sx={{ minWidth: 280 }}
              />
            );
          }
          return (
            <Typography variant="body2">
              {params.row.name} ({params.value})
            </Typography>
          );
        },
      },
    ];

    if (config.hasAssociationValue) {
      baseColumns.push({
        field: 'value',
        headerName: 'Value',
        width: 180,
        renderCell: (params: GridRenderCellParams<AssociatedItemRowData<TAssociatedItem>>) => {
          const isNewRow = params.row.code.startsWith(NEW_ROW_CODE_PREFIX);
          const isDraft = params.row.status === 'draft';
          const changes = modifiedRows[params.row.code];
          const currentValue = changes && 'value' in changes ? changes.value : (params.value as string | null);

          if (isDraft || isNewRow) {
            return <Typography variant="body2">{currentValue || '-'}</Typography>;
          }

          return (
            <InlineTextFieldEditCell
              value={currentValue}
              onChange={(value) => {
                setModifiedRows((prev) => ({
                  ...prev,
                  [params.row.code]: { ...prev[params.row.code], value: value || null },
                }));
              }}
            />
          );
        },
      });
    }

    baseColumns.push(
      {
        field: 'catalogGroup',
        headerName: 'Group',
        width: 150,
        renderCell: (params: GridRenderCellParams<AssociatedItemRowData<TAssociatedItem>>) => (
          <Typography variant="body2">{params.value?.name ?? '-'}</Typography>
        ),
      },
      {
        field: 'displayOrder',
        headerName: 'Rank',
        width: 120,
        renderCell: (params: GridRenderCellParams<AssociatedItemRowData<TAssociatedItem>>) => {
          const isNewRow = params.row.code.startsWith(NEW_ROW_CODE_PREFIX);
          const isDraft = params.row.status === 'draft';
          const currentValue = isNewRow
            ? modifiedRows[params.row.code]?.displayOrder
            : (modifiedRows[params.row.code]?.displayOrder ?? params.value);

          if (isDraft && !isNewRow) {
            return <Typography variant="body2">{currentValue ?? '-'}</Typography>;
          }

          return (
            <InlineNumberEditCell
              value={currentValue}
              onChange={(value) => {
                setModifiedRows((prev) => ({
                  ...prev,
                  [params.row.code]: { ...prev[params.row.code], displayOrder: value } as PendingChanges,
                }));
              }}
            />
          );
        },
      },
      {
        field: 'defaultDisplayOrder',
        headerName: 'Default',
        width: 80,
        sortable: false,
        renderCell: (params: GridRenderCellParams<AssociatedItemRowData<TAssociatedItem>>) => (
          <Typography variant="body2" color="textSecondary">
            {params.value ?? '-'}
          </Typography>
        ),
      },
      {
        field: 'featured',
        headerName: 'Featured',
        flex: 0,
        width: 100,
        renderCell: (params: GridRenderCellParams<AssociatedItemRowData<TAssociatedItem>>) => {
          const isNewRow = params.row.code.startsWith(NEW_ROW_CODE_PREFIX);
          const isDraft = params.row.status === 'draft';
          const currentValue = isNewRow
            ? (modifiedRows[params.row.code]?.featured ?? null)
            : (modifiedRows[params.row.code]?.featured ?? params.value);

          if (isDraft && !isNewRow) {
            return <Typography variant="body2">{currentValue === null ? '-' : currentValue ? 'Yes' : 'No'}</Typography>;
          }

          return (
            <InlineCheckboxEditCell
              value={currentValue}
              onChange={(value) => {
                setModifiedRows((prev) => ({
                  ...prev,
                  [params.row.code]: { ...prev[params.row.code], featured: value } as PendingChanges,
                }));
              }}
            />
          );
        },
      },
      {
        field: 'defaultFeatured',
        headerName: 'Default',
        width: 80,
        sortable: false,
        renderCell: (params: GridRenderCellParams<AssociatedItemRowData<TAssociatedItem>>) => (
          <Typography variant="body2" color="textSecondary">
            {params.value == null ? '-' : params.value ? 'Yes' : 'No'}
          </Typography>
        ),
      },
      {
        field: 'actions',
        headerName: 'Actions',
        width: 180,
        sortable: false,
        renderCell: (params: GridRenderCellParams<AssociatedItemRowData<TAssociatedItem>>) => {
          const hasChanges = Boolean(modifiedRows[params.row.code]);
          const isSaving = addMutation.isPending || updateMutation.isPending;
          const isDeleting = deletingCodes.has(params.row.code);
          const isNewRow = params.row.code.startsWith(NEW_ROW_CODE_PREFIX);
          const isDraft = params.row.status === 'draft';
          const hasOverrides =
            params.row.displayOrder != null ||
            params.row.featured != null ||
            (config.hasAssociationValue && 'value' in params.row && params.row.value != null);

          if (isDraft && !isNewRow) {
            return <Stack />;
          }

          return (
            <Stack direction="row" spacing={0.5}>
              <Tooltip title={isNewRow ? `Add ${config.itemName.toLowerCase()}` : 'Save changes'}>
                <span>
                  <IconButton
                    size="small"
                    onClick={() => handleSave(params.row)}
                    disabled={!hasChanges || isSaving || isDeleting}
                    sx={{ color: hasChanges && !isSaving ? blue[600] : undefined }}
                  >
                    {isSaving ? <CircularProgress size={18} /> : <SaveIcon />}
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Clear overrides">
                <span>
                  <IconButton
                    size="small"
                    onClick={() => handleClearOverrides(params.row)}
                    disabled={isNewRow || !hasOverrides || isSaving || isDeleting}
                  >
                    <RestoreIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={isNewRow ? 'Remove row' : 'Clear changes'}>
                <span>
                  <IconButton
                    size="small"
                    onClick={() => handleClearChanges(params.row.code)}
                    disabled={(!hasChanges && !isNewRow) || isSaving || isDeleting}
                  >
                    <ClearIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={isNewRow ? 'Remove row' : `Remove ${config.itemName.toLowerCase()}`}>
                <span>
                  <IconButton
                    size="small"
                    onClick={() => handleDeleteClick(params.row)}
                    disabled={isSaving || isDeleting}
                    sx={{ color: red[600] }}
                  >
                    {isDeleting ? <CircularProgress size={18} /> : <DeleteIcon />}
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          );
        },
      },
    );

    return baseColumns;
  }, [
    allItems,
    availableItemOptions,
    handleSave,
    handleClearOverrides,
    handleClearChanges,
    handleDeleteClick,
    addMutation.isPending,
    updateMutation.isPending,
    deletingCodes,
    modifiedRows,
    config,
    itemCodeKey,
  ]);

  // Combine local new rows with API rows (local rows first to appear at top)
  const allRows = useMemo(() => {
    return [...localRows, ...associatedItems];
  }, [localRows, associatedItems]);

  // Catalog group options for filter dropdown
  const catalogGroupOptions: Array<CatalogGroupOption> = useMemo(
    () =>
      catalogGroups.map((group) => ({
        id: group.id,
        name: group.name,
        code: group.code,
      })),
    [catalogGroups],
  );

  const selectedFilterGroup = catalogGroupOptions.find((opt) => opt.id === filterCatalogGroupId) ?? null;

  const handleSearchSubmit = useCallback((event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setPage(0);
  }, []);

  if (error) {
    return (
      <Alert severity="error">
        Error loading {config.associationContext} {config.itemNamePlural.toLowerCase()}:{' '}
        {error instanceof Error ? error.message : 'Unknown error'}
      </Alert>
    );
  }

  return (
    <Stack direction="column" spacing={2}>
      <Stack direction="row" spacing={2} alignItems="center">
        <Typography
          variant={config.associationContext === 'property' ? 'h6' : 'subtitle2'}
          color={config.associationContext === 'room' ? 'textSecondary' : undefined}
        >
          {config.associationContextName
            ? `${config.itemNamePlural} for ${config.associationContextName}`
            : `${config.associationContext === 'property' ? 'Property' : ''} ${config.itemNamePlural}`}
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        {filterStatus === 'approved' && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddNew}>
            Add {config.itemName}
          </Button>
        )}
      </Stack>

      <Typography variant="body2" color="textSecondary">
        {filterStatus === 'approved' ? (
          `Manage ${config.itemNamePlural.toLowerCase()} associated with this ${config.associationContext}. Click Add ${
            config.itemName
          } to associate a new ${config.itemName.toLowerCase()}, or modify Rank and Featured status for existing ${config.itemNamePlural.toLowerCase()}.`
        ) : (
          <>
            Draft {config.itemNamePlural.toLowerCase()} can only be managed from{' '}
            <MuiLink
              href={`/accommodation/properties#${config.itemNamePlural.toLowerCase()}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              the main {config.itemNamePlural.toLowerCase()} interface
            </MuiLink>
            .
          </>
        )}
      </Typography>

      <Box component="form" onSubmit={handleSearchSubmit}>
        <Stack direction="row" spacing={2} alignItems="center">
          <TextField
            size="small"
            label="Search by name"
            value={searchName}
            onChange={(e) => setSearchName(e.target.value)}
            sx={{ minWidth: 200 }}
          />
          <Autocomplete
            size="small"
            options={catalogGroupOptions}
            value={selectedFilterGroup}
            onChange={(_event, newValue) => {
              setFilterCatalogGroupId(newValue?.id);
              setPage(0);
            }}
            getOptionLabel={(option) => option.name}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={(params) => <TextField {...params} label="Filter by Catalog Group" variant="outlined" />}
            sx={{ minWidth: 250 }}
          />
          <TextField
            size="small"
            select
            label="Status"
            value={filterStatus}
            onChange={(e) => {
              setFilterStatus(e.target.value as CatalogItemStatus);
              setPage(0);
            }}
            sx={{ minWidth: 120 }}
          >
            <MenuItem value="approved">Approved</MenuItem>
            <MenuItem value="draft">Draft</MenuItem>
          </TextField>
          <Button variant="contained" type="submit">
            Search
          </Button>
          <Button
            variant="text"
            onClick={() => {
              setSearchName('');
              setFilterCatalogGroupId(undefined);
              setFilterStatus('approved');
              setPage(0);
            }}
          >
            Clear
          </Button>
        </Stack>
      </Box>

      <Table
        columns={columns}
        rows={allRows}
        getRowId={(row) => row.code}
        paginationMode="server"
        paginationModel={{ page, pageSize }}
        onPaginationModelChange={(model) => setPage(model.page)}
        totalRowCount={totalCount + localRows.length}
        isLoading={isLoading}
        autoHeight
        dataGridProps={{
          apiRef,
          editMode: 'cell',
          processRowUpdate,
          onProcessRowUpdateError: (updateError) => {
            enqueueSnackbar(`Error processing row update: ${updateError.message}`, { variant: 'error' });
          },
          getRowHeight: () => 'auto',
          sx: {
            '& .MuiDataGrid-cell': {
              py: 1,
              display: 'flex',
              alignItems: 'center',
            },
          },
        }}
      />
    </Stack>
  );
}
